/**
 * @fileoverview Client portal onboarding tour controller.
 *
 * Provides guided, popup-based tours (powered by driver.js) for each client
 * portal page. On a user's very first visit, an opening prompt asks whether
 * they want a tour:
 *   - "Take a tour" runs the Dashboard tour, and each other page's tour then
 *     auto-runs once on first visit.
 *   - "Maybe later" turns off all first-visit tours (marks every page seen)
 *     and shows a one-time hint pointing at the profile menu, where a tour
 *     can be replayed anytime via `startCurrentTour`.
 * Completion is persisted per-user via useUserPreferences so tours never
 * replay unprompted.
 *
 * Mounted once inside ClientPortal (must be within the Router so it can read
 * the current location). Tours degrade gracefully: steps whose target element
 * isn't present are skipped, and auto-start waits for the page's content to
 * finish loading before running.
 *
 * @requires driver.js
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useUserPreferences } from "../hooks/useUserPreferences";
import {
  TOUR_STEPS,
  SEEN_KEY_BY_PATH,
  READY_SELECTOR_BY_PATH,
  replayStep,
} from "./tourSteps";
import "./onboarding.css";

/** Route whose first visit shows the opening "take a tour?" prompt. */
const GATE_PATH = "/dashboard";

const OnboardingContext = createContext({
  startCurrentTour: () => {},
});

/**
 * Hook to access onboarding tour actions.
 * @returns {{ startCurrentTour: () => void }}
 */
export const useOnboarding = () => useContext(OnboardingContext);

/** Max polling attempts (~6s) waiting for a page's ready selector. */
const MAX_READY_ATTEMPTS = 40;
const READY_POLL_MS = 150;
const AUTO_START_DELAY_MS = 400;

/**
 * Resolve a tour's raw step definitions into driver.js steps against the
 * live DOM. Centered steps (no `element`) are kept as-is; element steps are
 * resolved (selector string or resolver fn) and dropped if not present.
 *
 * @param {Array<Object>} rawSteps
 * @returns {Array<Object>} Steps with concrete elements, missing ones removed
 */
function resolveSteps(rawSteps) {
  const out = [];
  for (const step of rawSteps) {
    if (!("element" in step) || step.element == null) {
      out.push(step);
      continue;
    }
    const el =
      typeof step.element === "function"
        ? step.element()
        : document.querySelector(step.element);
    if (el) out.push({ ...step, element: el });
  }
  return out;
}

/** Extra breathing room (px) below the sticky navbar when scrolling. */
const NAV_SCROLL_GAP = 16;

/**
 * Current height of the sticky portal navbar, used to offset tour scrolls
 * so highlighted elements never land underneath it. Measured live so it's
 * correct for both desktop (~80px) and mobile (shorter top bar) layouts.
 *
 * @returns {number} Offset in pixels (nav height + gap)
 */
function getNavOffset() {
  const nav = document.querySelector(".cn-wrapper");
  const height = nav ? nav.getBoundingClientRect().height : 0;
  return height + NAV_SCROLL_GAP;
}

/**
 * Opening prompt shown on a user's first visit, asking whether they'd like
 * a tour. Styled to match the tour popovers (see onboarding.css).
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the prompt is visible
 * @param {() => void} props.onAccept - "Take a tour" handler
 * @param {() => void} props.onDecline - "Maybe later" handler
 */
function TourGate({ open, onAccept, onDecline }) {
  if (!open) return null;

  return (
    <div
      className="rsh-tour-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rsh-tour-gate-title"
    >
      <div className="rsh-tour-gate">
        <h2 id="rsh-tour-gate-title" className="rsh-tour-gate-title">
          Welcome to your portal! 👋
        </h2>
        <p className="rsh-tour-gate-text">
          Would you like a quick guided tour to see how everything works? You
          can always take it later from the profile menu.
        </p>
        <div className="rsh-tour-gate-actions">
          <button
            type="button"
            className="rsh-tour-gate-btn rsh-tour-gate-btn--secondary"
            onClick={onDecline}
          >
            Maybe later
          </button>
          <button
            type="button"
            className="rsh-tour-gate-btn rsh-tour-gate-btn--primary"
            onClick={onAccept}
          >
            Take a tour
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Onboarding provider. Wrap the client portal so descendants (e.g. Navbar)
 * can trigger tours and first-visit tours auto-run.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function OnboardingProvider({ children }) {
  const { pathname } = useLocation();
  const { preferences, updatePreferences, isLoading } = useUserPreferences();

  const driverRef = useRef(null);
  // Paths auto-started this session, so we don't re-trigger before the
  // persisted flag round-trips through Firestore.
  const autoStartedRef = useRef(new Set());
  const [gateOpen, setGateOpen] = useState(false);

  /**
   * Low-level driver.js runner shared by page tours and the one-off replay
   * hint. Handles the sticky-navbar scroll offset and cleanup, and invokes
   * `onFinish` when the tour is dismissed.
   *
   * @param {Array<Object>} steps - Already-resolved driver.js steps
   * @param {Object} [opts]
   * @param {() => void} [opts.onFinish] - Called once the tour is destroyed
   */
  const runSteps = useCallback((steps, { onFinish } = {}) => {
    if (!steps || steps.length === 0) return;

    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }

    // Elements we've applied a temporary scroll offset to, cleaned up
    // when the tour ends so we don't affect the app's own scrolling.
    const offsetEls = new Set();

    const driverObj = driver({
      showProgress: steps.length > 1,
      allowClose: true,
      overlayColor: "rgba(15, 23, 42, 0.6)",
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: "rsh-tour",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      steps,
      // Runs before driver.js scrolls the element into view. We give the
      // target a scroll-margin matching the sticky navbar so driver's
      // scrollIntoView leaves room for it; and if the element is already
      // tucked under the navbar (so driver would skip scrolling), we nudge
      // it down ourselves.
      onHighlightStarted: (el) => {
        if (!el) return;
        const offset = getNavOffset();
        el.style.scrollMarginTop = `${offset}px`;
        offsetEls.add(el);
        if (el.getBoundingClientRect().top < offset) {
          el.scrollIntoView({ block: "start", behavior: "auto" });
        }
      },
      onDestroyed: () => {
        driverRef.current = null;
        offsetEls.forEach((el) => {
          el.style.scrollMarginTop = "";
        });
        offsetEls.clear();
        if (onFinish) onFinish();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, []);

  /**
   * Build and run the tour for a given path.
   * @param {string} path - Route path
   * @param {Object} [opts]
   * @param {boolean} [opts.markSeen=false] - Persist completion on finish
   * @param {boolean} [opts.skipIntro=false] - Drop the leading centered
   *   intro step (used when the opening prompt already served as the intro)
   */
  const startTour = useCallback(
    (path, { markSeen = false, skipIntro = false } = {}) => {
      const rawSteps = TOUR_STEPS[path];
      if (!rawSteps || rawSteps.length === 0) return;

      let steps = resolveSteps(rawSteps);
      if (skipIntro && steps.length > 0 && !("element" in steps[0])) {
        steps = steps.slice(1);
      }
      if (steps.length === 0) return;

      runSteps(steps, {
        onFinish: () => {
          if (markSeen) {
            const seenKey = SEEN_KEY_BY_PATH[path];
            if (seenKey && !preferences[seenKey]) {
              updatePreferences({ [seenKey]: true }).catch(() => {});
            }
          }
        },
      });
    },
    [preferences, updatePreferences, runSteps],
  );

  /** Mark every page's tour as seen (used when the user declines). */
  const markAllSeen = useCallback(() => {
    const allSeen = {};
    Object.values(SEEN_KEY_BY_PATH).forEach((key) => {
      allSeen[key] = true;
    });
    updatePreferences(allSeen).catch(() => {});
  }, [updatePreferences]);

  /** Show the standalone "where to replay a tour" hint. */
  const showReplayHint = useCallback(() => {
    const steps = resolveSteps([replayStep]);
    runSteps(steps);
  }, [runSteps]);

  /** "Take a tour" — run the Dashboard tour (the prompt was the intro). */
  const handleGateAccept = useCallback(() => {
    setGateOpen(false);
    startTour(GATE_PATH, { markSeen: true, skipIntro: true });
  }, [startTour]);

  /** "Maybe later" — silence all tours and point out where to replay. */
  const handleGateDecline = useCallback(() => {
    setGateOpen(false);
    markAllSeen();
    showReplayHint();
  }, [markAllSeen, showReplayHint]);

  /** Replay the current page's tour on demand (ignores the seen flag). */
  const startCurrentTour = useCallback(() => {
    const path = pathname;
    if (!TOUR_STEPS[path]) return;

    const ready = READY_SELECTOR_BY_PATH[path];
    if (!ready || document.querySelector(ready)) {
      startTour(path, { markSeen: true });
      return;
    }
    // Content not ready yet — start as soon as it is.
    let attempt = 0;
    const poll = () => {
      if (document.querySelector(ready) || attempt >= MAX_READY_ATTEMPTS) {
        startTour(path, { markSeen: true });
        return;
      }
      attempt += 1;
      setTimeout(poll, READY_POLL_MS);
    };
    poll();
  }, [pathname, startTour]);

  // Auto-start the first-visit tour for the current page.
  useEffect(() => {
    if (isLoading) return undefined;

    const seenKey = SEEN_KEY_BY_PATH[pathname];
    if (!seenKey) return undefined;
    if (preferences[seenKey]) return undefined;
    if (autoStartedRef.current.has(pathname)) return undefined;

    let cancelled = false;
    let timer;
    const ready = READY_SELECTOR_BY_PATH[pathname];

    const tryStart = (attempt) => {
      if (cancelled) return;
      if (!ready || document.querySelector(ready) || attempt >= MAX_READY_ATTEMPTS) {
        autoStartedRef.current.add(pathname);
        // The Dashboard's first visit asks first; other pages run directly
        // (they only reach here when the user accepted the opening prompt).
        if (pathname === GATE_PATH) {
          setGateOpen(true);
        } else {
          startTour(pathname, { markSeen: true });
        }
        return;
      }
      timer = setTimeout(() => tryStart(attempt + 1), READY_POLL_MS);
    };

    timer = setTimeout(() => tryStart(0), AUTO_START_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pathname, isLoading, preferences, startTour]);

  // Tear down any running tour and close the prompt when leaving a page.
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
      setGateOpen(false);
    };
  }, [pathname]);

  return (
    <OnboardingContext.Provider value={{ startCurrentTour }}>
      {children}
      <TourGate
        open={gateOpen}
        onAccept={handleGateAccept}
        onDecline={handleGateDecline}
      />
    </OnboardingContext.Provider>
  );
}

export default OnboardingProvider;
